import React, { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Inline stock preview for the Equipment_Needed textarea.
 * Splits text by commas / newlines / semicolons, fuzzy-matches each token
 * against `inventory_items.Item_Name`, and renders a small dot per token:
 *   green = stock OK, amber = reorder zone, red = at/below low threshold, gray = no match
 */
let _cache = null;
async function loadInventory() {
  if (_cache) return _cache;
  try {
    const { data } = await api.get("/inventory", { params: { limit: 2000 } });
    _cache = data;
    return data;
  } catch {
    _cache = [];
    return _cache;
  }
}

function fuzzyMatch(token, items) {
  const t = token.trim().toLowerCase();
  if (t.length < 2) return null;
  let best = null;
  let bestScore = 0;
  for (const it of items) {
    const name = (it.Item_Name || "").toLowerCase();
    if (!name) continue;
    let score = 0;
    if (name === t) score = 100;
    else if (name.includes(t) || t.includes(name)) score = 60 + Math.min(name.length, t.length);
    else {
      // partial token match
      const tokens = name.split(/\s+/);
      const tTokens = t.split(/\s+/);
      const overlap = tokens.filter((x) => tTokens.some((y) => y && (x.startsWith(y) || y.startsWith(x)))).length;
      if (overlap > 0) score = 20 * overlap;
    }
    if (score > bestScore) {
      bestScore = score;
      best = it;
    }
  }
  return bestScore >= 30 ? best : null;
}

const DOT = {
  red: "bg-rose-700",
  yellow: "bg-amber-500",
  green: "bg-emerald-600",
  gray: "bg-slate-300",
};

export default function EquipmentStockPreview({ text }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [toolAvail, setToolAvail] = useState({});

  useEffect(() => {
    let mounted = true;
    loadInventory().then((d) => {
      if (mounted) {
        setItems(d || []);
        setLoaded(true);
      }
    });
    return () => { mounted = false; };
  }, []);

  // Fetch live tool availability whenever the text changes (debounced)
  useEffect(() => {
    if (!text || !text.trim()) {
      setToolAvail({});
      return;
    }
    const tokens = text
      .split(/[,\n;]/)
      .map((t) => t.trim())
      .filter((t) => t.length >= 2);
    if (tokens.length === 0) return;
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/equipment/availability", {
          params: { names: tokens.join(",") },
        });
        setToolAvail(data || {});
      } catch {
        setToolAvail({});
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [text]);

  if (!text || !text.trim()) return null;

  const tokens = text
    .split(/[,\n;]/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 2);

  if (tokens.length === 0) return null;

  const previews = tokens.map((tok) => {
    const match = loaded ? fuzzyMatch(tok, items) : null;
    const level = match ? (match.stock_level || "gray") : "gray";
    const tools = toolAvail[tok];
    return { tok, match, level, tools };
  });

  // Don't render if nothing matched and we already loaded
  if (loaded && previews.every((p) => !p.match && !(p.tools && p.tools.total > 0))) return null;

  return (
    <div className="mt-2 space-y-1" data-testid="equipment-stock-preview">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
        Live availability (Inventory + Tools)
      </div>
      <div className="flex flex-wrap gap-1.5">
        {previews.map((p, i) => (
          <span
            key={`${p.tok}-${i}`}
            data-testid={`stock-pill-${i}`}
            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border text-xs ${
              p.match || (p.tools && p.tools.total > 0)
                ? "bg-white border-slate-200 text-slate-800"
                : "bg-slate-50 border-slate-200 text-slate-500 italic"
            }`}
            title={
              p.match
                ? `Matched: ${p.match.Item_Name} — ${p.match.Current_Stock} in stock (low ≤ ${p.match.Low_Stock_Threshold})`
                : (p.tools && p.tools.total > 0)
                ? `${p.tools.available} of ${p.tools.total} tools available`
                : "Not tracked"
            }
          >
            <span className={`h-2 w-2 rounded-full ${DOT[p.level]}`} />
            <span className="font-bold">{p.tok}</span>
            {p.match && (
              <span className="text-slate-500">· {p.match.Current_Stock} in stock</span>
            )}
            {p.tools && p.tools.total > 0 && (
              <span
                data-testid={`tool-availability-${i}`}
                className={`ml-1 px-1.5 py-0.5 rounded-sm border text-[10px] font-bold uppercase tracking-wider ${
                  p.tools.available > 0
                    ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                    : "bg-rose-50 border-rose-300 text-rose-800"
                }`}
              >
                {p.tools.available}/{p.tools.total} tool{p.tools.total === 1 ? "" : "s"}
              </span>
            )}
            {!p.match && (!p.tools || p.tools.total === 0) && loaded && <span>— not tracked</span>}
          </span>
        ))}
      </div>
    </div>
  );
}
